import type { KiasiLiveBarService, LiveBarProfile, LiveBarRequest, LiveBarStatus, LiveBarSubscription } from './index';
import {
    barCommitted,
    barWithdrawn,
    connectStarted,
    createInitialLiveBarConnectionState,
    DEFAULT_RECONNECT_POLICY,
    disposed,
    lateFrame,
    liveBarConnectionReducer,
    profileSelected,
    sessionEnded,
    tokenRequired,
    transportStatus,
} from './state';
import type { LiveBarConnectionAction, LiveBarConnectionState, ReconnectPolicy } from './state';
import { computed, effect, signal, untracked } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import { connectBarStream } from '@thinkalpha/kiasi-wire';
import type { BarStreamConnection, StateBarView } from '@thinkalpha/kiasi-wire';
import { isStateUpdate, UpdateKind } from '@thinkalpha/kiasi-wire/reactive';
import { DeliveryProfile, LEVEL_STRIDE } from '@thinkalpha/kiasi-wire/views';
import { DisposableBox } from '@thinkalpha/ui-core/DisposableBox';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import { resolveKiasiLiveUrls, scaledPriceToDollars } from 'src/lib/kiasi/bars';
import type { VolumeBarSnapshot, VolumePriceLevel } from 'src/lib/kiasi/bars';
import type { EffectiveSession } from 'src/services/SessionService/effectiveSession';

export type { ReconnectPolicy } from './state';

export interface KiasiLiveBarServiceDeps {
    /** The token a backend should see; a masquerade wins. Identity changes re-authenticate the stream. */
    readonly session: EffectiveSession;
    readonly logger: Logger;
    /** HTTP base the live endpoints derive from. */
    readonly kiasiApi: string;
    /** Resolves a relative HTTP base (the dev-server proxy). */
    readonly origin: string;
    /** Try HTTP/3 WebTransport before WebSocket on an HTTPS host. */
    readonly preferWebTransport?: boolean;
    /** Injectable for tests; defaults to the wire client. */
    readonly connect?: typeof connectBarStream;
    readonly maxLevels?: number;
    readonly reconnect?: Partial<ReconnectPolicy>;
}

const DEFAULT_MAX_LEVELS = 2048;

type Connect = typeof connectBarStream;

const DELIVERY_PROFILE_NAMES = new Map<number, string>(
    Object.entries(DeliveryProfile).map(([name, value]) => [value, name]),
);

/** The profile the receiver has selected; the wire client sets it before reporting the status that follows. */
function selectedProfile(connection: BarStreamConnection): LiveBarProfile | null {
    const profile = connection.profile;
    if (profile === null) return null;
    return {
        name: DELIVERY_PROFILE_NAMES.get(profile) ?? String(profile),
        cadenceMillis: connection.profiles.find((config) => config.profile === profile)?.cadenceMillis ?? null,
    };
}

function copyBar(
    view: StateBarView,
    request: LiveBarRequest,
    levelBuffer: Float64Array,
    maxLevels: number,
): VolumeBarSnapshot {
    const count = Math.min(view.levelsLength, maxLevels);
    view.readLevelsInto(0, count, levelBuffer);
    const levels: VolumePriceLevel[] = new Array(count);
    for (let index = 0; index < count; index++) {
        const at = index * LEVEL_STRIDE;
        const bid = levelBuffer[at + 1];
        const ask = levelBuffer[at + 2];
        const mid = levelBuffer[at + 3];
        levels[index] = {
            price: scaledPriceToDollars(levelBuffer[at]),
            bid,
            ask,
            mid,
            premarket: levelBuffer[at + 4],
            regular: levelBuffer[at + 5],
            postmarket: levelBuffer[at + 6],
            total: bid + ask + mid,
        };
    }
    const hasPrice = view.hasPrice;
    return {
        symbol: request.symbol,
        interval: request.interval,
        openTimeMs: view.barOpenTsMs,
        closed: view.closed,
        sequence: view.sequence,
        priceCarried: view.priceCarried,
        open: hasPrice ? scaledPriceToDollars(view.open) : null,
        high: hasPrice ? scaledPriceToDollars(view.high) : null,
        low: hasPrice ? scaledPriceToDollars(view.low) : null,
        close: hasPrice ? scaledPriceToDollars(view.close) : null,
        tradeCount: view.tradeCount,
        totalVolume: view.totalVol,
        vwapVolume: view.vwapVolume,
        vwapNotional: scaledPriceToDollars(view.vwapNotional),
        vwap: view.vwapVolume > 0 ? scaledPriceToDollars(view.vwap) : null,
        poc: scaledPriceToDollars(view.poc),
        vah: scaledPriceToDollars(view.vah),
        val: scaledPriceToDollars(view.val),
        delta: view.delta,
        bidVolume: view.bidVol,
        askVolume: view.askVol,
        midVolume: view.midVol,
        premarketVolume: view.preVol,
        regularVolume: view.regVol,
        postmarketVolume: view.postVol,
        openAuctionVolume: view.openAuctionVol,
        closeAuctionVolume: view.closeAuctionVol,
        levels,
    };
}

/** One live session: the wire connection plus its listener. Disposal closes it. */
class ConnectionAttempt implements Disposable {
    readonly connection: BarStreamConnection;
    readonly #unsubscribe: () => void;

    constructor(connection: BarStreamConnection, listener: (kind: UpdateKind) => void) {
        this.connection = connection;
        this.#unsubscribe = connection.updates.subscribe((_revision, kind) => listener(kind));
    }

    [Symbol.dispose](): void {
        this.#unsubscribe();
        void this.connection.close().catch(() => undefined);
    }
}

/** A pending reconnect. Disposal cancels it. */
class ReconnectTimer implements Disposable {
    readonly #handle: ReturnType<typeof setTimeout>;

    constructor(delayMs: number, fire: () => void) {
        this.#handle = setTimeout(fire, delayMs);
    }

    [Symbol.dispose](): void {
        clearTimeout(this.#handle);
    }
}

class LiveBarSubscriptionImpl implements LiveBarSubscription {
    readonly #state = signal<LiveBarConnectionState>(createInitialLiveBarConnectionState());
    readonly #bar = computed(() => this.#state.value.bar);
    readonly #status = computed(() => this.#state.value.status);
    readonly #statusMessage = computed(() => this.#state.value.statusMessage);
    readonly #profile = computed(() => this.#state.value.profile);
    readonly #closedRevision = computed(() => this.#state.value.closedRevision);
    readonly #stack = new DisposableStack();
    /** At most one open session and at most one pending reconnect. */
    readonly #attempt = this.#stack.use(new DisposableBox<ConnectionAttempt>());
    readonly #timer = this.#stack.use(new DisposableBox<ReconnectTimer>());
    readonly #levelBuffer: Float64Array;
    readonly #request: LiveBarRequest;
    readonly #deps: KiasiLiveBarServiceDeps;
    readonly #connect: Connect;
    readonly #maxLevels: number;
    readonly #policy: ReconnectPolicy;

    constructor(request: LiveBarRequest, deps: KiasiLiveBarServiceDeps) {
        this.#request = request;
        this.#deps = deps;
        this.#connect = deps.connect ?? connectBarStream;
        this.#maxLevels = deps.maxLevels ?? DEFAULT_MAX_LEVELS;
        this.#policy = { ...DEFAULT_RECONNECT_POLICY, ...deps.reconnect };
        this.#levelBuffer = new Float64Array(this.#maxLevels * LEVEL_STRIDE);

        // Connect once a token exists. A token that arrives while waiting, or after the
        // subscription gave up, starts a session; a refreshed token for the same identity leaves
        // an open session alone, while a new identity (a masquerade starting or ending) replaces it.
        // A token that goes away is a real transition: the session ends until one returns.
        let lastIdentity = this.#deps.session.identity.peek();
        this.#stack.defer(
            effect(() => {
                const token = this.#deps.session.accessToken.value;
                const identity = this.#deps.session.identity.value;
                untracked(() => {
                    const identityChanged = identity !== lastIdentity;
                    lastIdentity = identity;
                    const status = this.#state.value.status;
                    if (status === 'disposed') return;
                    if (token === null) {
                        this.#attempt.clear();
                        this.#timer.clear();
                        this.#dispatch(tokenRequired());
                        return;
                    }
                    if (status === 'unauthenticated' || status === 'error' || identityChanged) this.#open(token);
                });
            }),
        );
        this.#stack.defer(() => this.#dispatch(disposed()));
    }

    get bar(): ReadonlySignal<VolumeBarSnapshot | null> {
        return this.#bar;
    }

    get status(): ReadonlySignal<LiveBarStatus> {
        return this.#status;
    }

    get statusMessage(): ReadonlySignal<string | null> {
        return this.#statusMessage;
    }

    get profile(): ReadonlySignal<LiveBarProfile | null> {
        return this.#profile;
    }

    get closedRevision(): ReadonlySignal<number> {
        return this.#closedRevision;
    }

    #dispatch(action: LiveBarConnectionAction): void {
        this.#state.value = liveBarConnectionReducer(this.#state.peek(), action);
    }

    #open(token: string): void {
        this.#timer.clear();
        this.#attempt.clear();
        this.#dispatch(connectStarted());
        let connection: BarStreamConnection;
        try {
            const { url, websocketUrl } = resolveKiasiLiveUrls(this.#deps.kiasiApi, this.#deps.origin, {
                preferWebTransport: this.#deps.preferWebTransport ?? false,
            });
            connection = this.#connect({
                url,
                websocketUrl,
                token,
                symbol: this.#request.symbol,
                interval: this.#request.interval,
                maxLevels: this.#maxLevels,
                // Every profile selection, the first and each adaptive switch, is followed by a status.
                onStatus: (status) => {
                    if (this.#attempt.current?.connection === connection) {
                        this.#dispatch(transportStatus(status.message, status.phase === 'streaming'));
                        this.#dispatch(profileSelected(selectedProfile(connection)));
                    }
                },
                onLate: () => {
                    if (this.#attempt.current?.connection === connection) this.#dispatch(lateFrame());
                },
            });
        } catch (error: unknown) {
            this.#fail(error);
            return;
        }
        const attempt = new ConnectionAttempt(connection, (kind) => this.#onUpdate(attempt, kind));
        this.#attempt.next(attempt);
        connection.ready.catch((error: unknown) => {
            if (this.#attempt.current === attempt) this.#fail(error);
        });
    }

    /** Runs inside the stream's synchronous notification; the view is borrowed only until it returns. */
    #onUpdate(attempt: ConnectionAttempt, kind: UpdateKind): void {
        if (this.#attempt.current !== attempt) return;
        if (kind === UpdateKind.Terminated) {
            this.#endSession('stream ended');
            return;
        }
        if (kind === UpdateKind.LateClose || kind === UpdateKind.LateRemoved) {
            this.#dispatch(lateFrame());
            return;
        }
        if (!isStateUpdate(kind)) return;
        const view = attempt.connection.updates.read();
        if (view === null) {
            this.#dispatch(barWithdrawn(kind === UpdateKind.Removed));
            return;
        }
        const final = kind === UpdateKind.Closed || kind === UpdateKind.Correction;
        this.#dispatch(barCommitted(copyBar(view, this.#request, this.#levelBuffer, this.#maxLevels), final));
    }

    #fail(error: unknown): void {
        const message = error instanceof Error ? error.message : String(error);
        this.#deps.logger.warn(
            `Kiasi live bar stream ${this.#request.symbol}/${this.#request.interval} failed: ${message}`,
            { symbol: this.#request.symbol, interval: this.#request.interval, error },
        );
        this.#endSession(message);
    }

    #endSession(reason: string): void {
        this.#attempt.clear();
        this.#dispatch(sessionEnded(reason, this.#policy));
        const state = this.#state.peek();
        if (state.status !== 'reconnecting') return;
        this.#timer.next(
            new ReconnectTimer(state.reconnectDelayMs, () => {
                this.#timer.release();
                const token = this.#deps.session.accessToken.peek();
                if (token === null) this.#dispatch(tokenRequired());
                else this.#open(token);
            }),
        );
    }

    [Symbol.dispose](): void {
        this.#stack.dispose();
    }
}

export function createKiasiLiveBarService(deps: KiasiLiveBarServiceDeps): KiasiLiveBarService {
    return {
        subscribe(request: LiveBarRequest): LiveBarSubscription {
            return new LiveBarSubscriptionImpl(request, deps);
        },
    };
}
